import api from "./api";

export type ApiError =
  | { type: "serverValidation"; errors?: Record<string, string> }
  | { type: "network"; message?: string }
  | { type: "server"; message?: string };
export interface BookingTime {
  id: number;
  company_id: number;
  location_id: number;
  day: string;
  start_time: string;
  end_time: string;
  multiple_booking: string;
  max_booking: number;
  per_day_max_booking: number;
  per_slot_max_booking: number;
  status: string;
  slot_duration: number;
  created_at: string | null;
  updated_at: string | null;
  company?: Settings;
}

export interface Timezone {
  id: number;
  country_id: number;
  zone_name: string;
  name: string;
  gmt_offset: string;
  gmt_offset_name: string;
  abbreviation: string;
}

export interface LocationDetails {
  id: number;
  name: string;
  lat: number;
  lng: number;
  country_id: number;
  timezone_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  timezone: Timezone;
}

export interface ServiceLocationItem extends BookingTime {
  locations: LocationDetails;
}

export interface ImageItem {
  id: number;
  image: string;
  image_url: string;
}

export interface Tax {
  id: number;
  name: string;
  percent: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ReceiptFormat {
  name: string;
  width: string;
  height: string;
  description: string;
}

export interface Currency {
  id: number;
  currency_name: string;
  currency_symbol: string;
  currency_code: string;
  exchange_rate: number;
  usd_price: number | null;
  is_cryptocurrency: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface EnabledLanguage {
  id: number;
  language_code: string;
  language_name: string;
  status: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface SmtpSetting {
  id: number;
  mail_driver: string;
  mail_host: string;
  mail_port: string;
  mail_username: string;
  mail_password: string;
  mail_from_name: string;
  mail_from_email: string;
  mail_encryption: string;
  verified: number;
  created_at: string;
  updated_at: string;
  set_smtp_message: string | null;
}

export interface CredentialSetting {
  id: number;
  company_id: number;
  offline_payment: string;
  offline_commission: string | null;
  show_payment_options: string;
  paypal_client_id: string | null;
  paypal_secret: string | null;
  paypal_mode: string;
  paypal_status: string;
  paypal_commission_status: string;
  paypal_commission_percentage: string | null;
  paypal_partnership_details: string | null;
  stripe_client_id: string | null;
  stripe_secret: string | null;
  stripe_webhook_secret: string | null;
  stripe_status: string;
  stripe_commission_status: string;
  stripe_commission_percentage: string | null;
  razorpay_key: string | null;
  razorpay_secret: string | null;
  razorpay_webhook_secret: string | null;
  razorpay_status: string;
  razorpay_commission_status: string;
  razorpay_commission_percentage: string | null;
  created_at: string;
  updated_at: string;
  show_pay: boolean;
}

export interface SmsSetting {
  id: number;
  nexmo_status: string;
  nexmo_key: string | null;
  nexmo_secret: string | null;
  nexmo_from: string;
  created_at: string;
  updated_at: string;
  msg91_status: string;
  msg91_key: string | null;
  msg91_from: string;
}

export interface ZoomSetting {
  id: number;
  company_id: number;
  api_key: string | null;
  secret_key: string | null;
  purchase_code: string | null;
  meeting_app: string;
  supported_until: string | null;
  enable_zoom: string;
  created_at: string;
  updated_at: string;
}

export interface Role {
  id: number;
  company_id: number;
  name: string;
  display_name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface Module {
  id: number;
  name: string;
  display_name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface ModuleSetting {
  id: number;
  company_id: number;
  module_name: string;
  status: string;
  type: string;
  created_at: string;
  updated_at: string;
}

export interface Employee {
  id: number;
  company_id: number;
  group_id: number | null;
  name: string;
  email: string;
  address: string | null;
  date_of_birth: string | null;
  calling_code: string;
  mobile: string;
  cus_source: string | null;
  landline: string | null;
  mobile_verified: number;
  image: string | null;
  rtl: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  country_id: number | null;
  slug: string;
  skin_type: string | null;
  user_image_url: string;
  mobile_with_code: string;
  formatted_mobile: string;
}

export interface VendorPage {
  id: number;
  company_id: number;
  address: string;
  description: string;
  primary_contact: string;
  secondary_contact: string | null;
  photos: string | null;
  default_image: string | null;
  og_image: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  map_option: string;
  latitude: string;
  longitude: string;
  created_at: string;
  updated_at: string;
  images: string;
  photos_without_default_image: any[];
}

export interface Site {
  id: number;
  server_id: number;
  name: string;
  aliases: string[];
  directory: string;
  wildcards: boolean;
  status: string;
  repository: string | null;
  repository_provider: string | null;
  repository_branch: string | null;
  repository_status: string | null;
  quick_deploy: boolean;
  deployment_status: string | null;
  project_type: string;
  php_version: string;
  app: string | null;
  app_status: string | null;
  failure_deployment_emails: string | null;
  created_at: string;
  telegram_secret: string;
  username: string;
  deployment_url: string;
  is_secured: boolean;
  web_directory: string;
  slack_channel: string | null;
  telegram_chat_id: string | null;
  telegram_chat_title: string | null;
  teams_webhook_url: string | null;
  discord_webhook_url: string | null;
  tags: any[];
}

export interface Settings {
  id: number;
  currency_id: number | string;
  package_id: number;
  company_name: string;
  slug: string;
  template: string;
  subdomain: string;
  website_status: string;
  is_claim: string;
  custom_domain_status: string | null;
  custom_domain: string | null;
  company_email: string;
  company_phone: string;
  company_phone_2: string | null;
  contact_email: string;
  logo: string;
  address: string;
  locality: string;
  date_format: string;
  time_format: string;
  website: string;
  timezone: string;
  print_receipt: string;
  printer_name: string;
  receipt_format: string;
  locale: string;
  latitude: string | null;
  longitude: string | null;
  trial_ends_at: string | null;
  licence_expire_on: string;
  disable_slot: string;
  booking_time_type: string;
  booking_per_day: string | null;
  employee_selection: string;
  multi_task_user: string | null;
  popular_store: string;
  created_at: string;
  updated_at: string;
  status: string;
  verified: string;
  stripe_id: string | null;
  xonder_id: string | null;
  card_brand: string | null;
  card_last_four: string | null;
  package_type: string | null;
  cron_status: string;
  display_deal: string;
  approve_online_booking: string;
  approve_offline_booking: string;
  duration: number;
  duration_type: string;
  pay_with_xeconnect: string;
  vkb_is_visible: number | boolean | string;
  vkb_position: string;
  landline: string | null;
  show_contact_details: number | boolean;
  quick_payment_type: string;
  is_sunbed: number;
  is_sunbed_tmax: number;
  logo_url: string;
  formatted_phone_number: string;
  formatted_address: string;
  formatted_website: string;
  company_verification_url: string;
}

export interface SettingsResponse {
  success: boolean;
  data: {
    booking_times: BookingTime[];
    service_locations: Record<string, ServiceLocationItem>;
    service_at_location: Record<string, number>;
    images: ImageItem[];
    tax: Tax;
    timezones: string[];
    date_formats: Record<string, string>;
    receipt_formats: Record<string, ReceiptFormat>;
    time_formats: Record<string, string>;
    date_object: string;
    currencies: Currency[];
    enabled_languages: EnabledLanguage[];
    smtp_setting: SmtpSetting;
    credential_setting: CredentialSetting;
    sms_setting: SmsSetting;
    zoom_setting: ZoomSetting;
    roles: Role[];
    total_permissions: number;
    total_permission: number;
    modules: Module[];
    module_settings: ModuleSetting[];
    employees: Employee[];
    laravel_version: string;
    office_leaves: any[];
    stripe_payment_setting: any | null;
    razorpay_payment_setting: any | null;
    paypal_payment_setting: any | null;
    vendor_page: VendorPage;
    company_booking_notification: any[];
    site: Site | null;
    settings: Settings;
  };
}
let settingsCache: SettingsResponse | null = null;
let settingsPromise: Promise<SettingsResponse> | null = null;

export const getSettings = async (forceRefresh: boolean = false): Promise<SettingsResponse> => {
  if (settingsCache && !forceRefresh) {
    return settingsCache;
  }

  if (settingsPromise && !forceRefresh) {
    return settingsPromise;
  }

  settingsPromise = (async () => {
    try {
      const response = await api.get(`/admin/settings`, {
        headers: {
          Accept: "application/json",
        },
      });

      settingsCache = response.data as SettingsResponse;
      return settingsCache;
    } catch (err: any) {
      settingsPromise = null; // Clear promise on error so next attempt can retry
      if (!err.response) {
        throw {
          type: "network",
          message: "Network error. Please check your internet connection.",
        } as ApiError;
      }

      throw {
        type: "server",
        message: err.response?.data?.message || err.message,
      } as ApiError;
    } finally {
      // We keep settingsPromise until it's finished to avoid duplicate concurrent calls
      // but we might want to clear it after some time or just rely on settingsCache
      setTimeout(() => { settingsPromise = null; }, 5000); // Clear after 5 seconds to allow for future refreshes
    }
  })();

  return settingsPromise;
};

// Export a way to clear the cache if needed
export const clearSettingsCache = () => {
  settingsCache = null;
  settingsPromise = null;
};

export interface UpdateSettingsPayload {
  company_name: string;
  company_email: string;
  company_phone: string;
  company_phone_2: string;
  landline: string;
  address: string;
  website: string;
  date_format: string;
  time_format: string;
  currency_id: number;
  currency_code: string;
  currency_symbol: string;
  timezone: string;
  locale: string;
  receipt_format: string;
  vkb_is_visible: number | string | boolean;
  vkb_position: string;
  show_contact_details: number;
  // Printer Settings
  print_receipt: string;
  printer_name: string;
  // Sunbed Settings
  is_sunbed: number;
  is_sunbed_tmax: number;
}

export interface UpdateSettingsResponse {
  success: boolean;
  data: Settings & {
    currency?: Currency;
  };
  message: string;
}

export const updateSettings = async (
  id: number | string,
  data: UpdateSettingsPayload
): Promise<UpdateSettingsResponse> => {
  try {
    const response = await api.put(`/admin/settings/${id}`, data, {
      headers: {
        Accept: "application/json",
      },
    });

    console.log("updateSettings response:", response.data);
    clearSettingsCache();

    return response.data as UpdateSettingsResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export interface VerifyDomainResponse {
  success: boolean;
  data: {
    status: string;
    company: Settings;
  };
  message: string;
}

export const verifyDomain = async (
  domain?: string
): Promise<VerifyDomainResponse> => {
  try {
    const data = domain ? { custom_domain: domain } : {};
    const response = await api.post(`/admin/settings/verify-domain`, data, {
      headers: {
        Accept: "application/json",
      },
    });
    console.log("verifyDomain response:", response.data);

    return response.data as VerifyDomainResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};
export interface InstallDomainSslResponse {
  success: boolean;
  data: any;
  message: string;
}

export const installDomainSsl = async (
  id: number | string
): Promise<InstallDomainSslResponse> => {
  try {
    const response = await api.post(
      `/admin/settings/install-domain-ssl/${id}`,
      {},
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("installDomainSsl response:", response.data);

    return response.data as InstallDomainSslResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};
export interface UpdateQuickPaymentResponse {
  success: boolean;
  data: Settings;
  message: string;
}

export const updateQuickPayment = async (
  quickPaymentType: "stylist" | "direct"
): Promise<UpdateQuickPaymentResponse> => {
  try {
    const response = await api.post(
      `/admin/settings/quick-payment`,
      { quick_payment_type: quickPaymentType === "stylist" ? "active" : "inactive" },
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("updateQuickPayment response:", response.data);

    return response.data as UpdateQuickPaymentResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};
export interface UpdateBookingTimesPayload {
  booking_time_type: string;
  booking_per_day: number;
  disable_slot: number;
  employee_selection: string;
  multi_task_user: number;
  duration: number;
  duration_type: string;
  pay_with_xeconnect: string;
}

export interface UpdateBookingTimesResponse {
  success: boolean;
  data: Settings;
  message: string;
}

export const updateBookingTimes = async (
  data: UpdateBookingTimesPayload
): Promise<UpdateBookingTimesResponse> => {
  try {
    const response = await api.post(`/admin/settings/booking-times`, data, {
      headers: {
        Accept: "application/json",
      },
    });

    console.log("updateBookingTimes response:", response.data);
    clearSettingsCache();

    return response.data as UpdateBookingTimesResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

// Individual Booking Time Operations
export interface GetBookingTimeResponse {
  success: boolean;
  data: {
    booking_time: BookingTime;
    location: LocationDetails;
    location_timezone: string;
  };
  message: string;
}

export interface UpdateBookingTimePayload {
  start_time?: string;
  end_time?: string;
  multiple_booking?: string;
  max_booking?: number;
  per_day_max_booking?: number;
  per_slot_max_booking?: number;
  slot_duration?: number;
  status: string;
}

export interface UpdateBookingTimeResponse {
  success: boolean;
  data: BookingTime;
  message: string;
}

export const getBookingTime = async (id: number): Promise<GetBookingTimeResponse> => {
  try {
    const response = await api.get(`/admin/booking-times/${id}/edit`, {
      headers: {
        Accept: "application/json",
      },
    });

    return response.data as GetBookingTimeResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export const updateBookingTime = async (
  id: number,
  data: UpdateBookingTimePayload
): Promise<UpdateBookingTimeResponse> => {
  try {
    const response = await api.put(`/admin/booking-times/${id}`, data, {
      headers: {
        Accept: "application/json",
      },
    });

    return response.data as UpdateBookingTimeResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export interface UpdateXonderSettingPayload {
  xonder_id: string;
}

export interface UpdateXonderSettingResponse {
  success: boolean;
  message: string;
}

export const updateXonderSetting = async (
  data: UpdateXonderSettingPayload
): Promise<UpdateXonderSettingResponse> => {
  try {
    const response = await api.post(`/admin/settings/xonder-setting`, data, {
      headers: {
        Accept: "application/json",
      },
    });

    console.log("updateXonderSetting response:", response.data);

    return response.data as UpdateXonderSettingResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};


export interface UpdateVendorPagePayload {
  description: string;
  seo_description: string;
  seo_keywords: string;
  map_option?: string;
  latitude?: string | number;
  longitude?: string | number;
  og_image?: File | string; // File for upload or string for existing URL
  default_image?: string | number;
}

export const updateVendorPage = async (
  id: number,
  data: UpdateVendorPagePayload
): Promise<SettingsResponse> => {
  try {
    if (!id || id === 0) {
      throw new Error(`Invalid vendor page ID: ${id}`);
    }

    const endpoint = `/admin/vendor-page/update-vendor/${id}`;
    const isFileUpload = data.og_image instanceof File;

    if (isFileUpload) {
      const formData = new FormData();

      // Append text fields
      formData.append("description", data.description);
      formData.append("seo_description", data.seo_description);
      formData.append("seo_keywords", data.seo_keywords);

      // Append optional fields
      if (data.map_option) {
        formData.append("map_option", data.map_option);
      }
      if (data.latitude !== undefined) {
        formData.append("latitude", String(data.latitude));
      }
      if (data.longitude !== undefined) {
        formData.append("longitude", String(data.longitude));
      }
      if (data.default_image !== undefined) {
        formData.append("default_image", String(data.default_image));
      }

      // Append og_image file - must be a File object
      // Backend uses $request->hasFile('og_image') to check
      if (data.og_image instanceof File) {
        formData.append("og_image", data.og_image, data.og_image.name);
      }

      // Use POST with _method=PUT (Laravel method spoofing) - matches old system
      formData.append("_method", "PUT");

      // Use POST instead of PUT for FormData (required for file uploads)
      const response = await api.post(endpoint, formData, {
        headers: {
          Accept: "application/json",
        },
      });

      return response.data as SettingsResponse;
    } else {
      const jsonData: any = {
        description: data.description,
        seo_description: data.seo_description,
        seo_keywords: data.seo_keywords,
      };

      if (data.map_option) {
        jsonData.map_option = data.map_option;
      }
      if (data.latitude !== undefined) {
        jsonData.latitude = String(data.latitude);
      }
      if (data.longitude !== undefined) {
        jsonData.longitude = String(data.longitude);
      }
      if (data.default_image !== undefined) {
        jsonData.default_image = String(data.default_image);
      }

      const response = await api.put(endpoint, jsonData, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      return response.data as SettingsResponse;
    }
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

// Vendor Page Gallery Images
export interface UpdateVendorPageImagesPayload {
  vendor_page_id: number;
  file?: File[];
  uploaded_files?: string; // JSON string of existing image filenames
  default_image?: string;
}

export interface DeleteVendorPageImagePayload {
  vendorId: number;
  file: string; // Filename to delete
  default_image?: string;
}

export const updateVendorPageImages = async (
  data: UpdateVendorPageImagesPayload
): Promise<SettingsResponse> => {
  try {
    const formData = new FormData();
    formData.append("vendor_page_id", String(data.vendor_page_id));

    // Add new image files
    if (data.file && data.file.length > 0) {
      data.file.forEach((file) => {
        formData.append("file[]", file);
      });
    }

    // Send existing uploaded files as JSON string
    if (data.uploaded_files) {
      formData.append("uploaded_files", data.uploaded_files);
    }

    if (data.default_image) {
      formData.append("default_image", data.default_image);
    }

    const response = await api.post(`/admin/vendor-page/update-images`, formData, {
      headers: {
        Accept: "application/json",
      },
    });

    return response.data as SettingsResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export const deleteVendorPageImage = async (
  data: DeleteVendorPageImagePayload
): Promise<SettingsResponse> => {
  try {
    const formData = new FormData();
    formData.append("vendorId", String(data.vendorId));
    formData.append("file", data.file);

    if (data.default_image) {
      formData.append("default_image", data.default_image);
    }

    const response = await api.post(`/admin/vendor-page/delete-image`, formData, {
      headers: {
        Accept: "application/json",
      },
    });

    return response.data as SettingsResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export interface UpdateProfilePayload {
  name: string;
  email: string;
  password?: string;
  image?: string;
  calling_code: string;
  mobile_number: string;
}

export const updateProfile = async (data: UpdateProfilePayload): Promise<SettingsResponse> => {
  try {
    // Log request payload to verify image is included
    const payloadToLog = {
      ...data,
      image: data.image
        ? (data.image.substring(0, 50) + "..." + (data.image.length > 50 ? ` (${data.image.length} chars, type: ${data.image.startsWith('data:') ? 'base64' : 'url'})` : ""))
        : undefined,
    };
    console.log("updateProfile request payload:", payloadToLog);
    console.log("Image included:", !!data.image);
    console.log("Image length:", data.image?.length || 0);

    const response = await api.post(`/admin/update-profile`, data, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    console.log("updateProfile response:", response.data);
    return response.data as SettingsResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
}

export const removeBusinessAccount = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/admin/settings/remove-business-account`, {
      headers: {
        Accept: "application/json",
      },
    });

    console.log("removeBusinessAccount response:", response.data);

    return response.data;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export interface UpdateSunbedConfigsPayload {
  is_sunbed: "active" | "inactive";
  is_sunbed_tmax: "active" | "inactive";
}

export interface UpdateSunbedConfigsResponse {
  success: boolean;
  data: Settings;
  message: string;
}

export const updateSunbedConfigs = async (
  data: UpdateSunbedConfigsPayload
): Promise<UpdateSunbedConfigsResponse> => {
  try {
    const response = await api.post(`/admin/settings/sunbed-configs`, data, {
      headers: {
        Accept: "application/json",
      },
    });

    console.log("updateSunbedConfigs response:", response.data);

    return response.data as UpdateSunbedConfigsResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};
